import { api } from '@/lib/api'
import type { APIResponse } from '@/types/api'
import type { Company } from '@/types/company'

/**
 * Servicio para obtener empresas
 */
export class CompaniesService {
	/**
	 * Obtiene todas las empresas
	 */
	static async getCompanies(): Promise<Company[]> {
		const response = await api.get<APIResponse<Company[]>>('/companies')
		const apiResponse = response.data

		// Verificar si la respuesta es exitosa
		if (!apiResponse.success || !apiResponse.data) {
			throw new Error(apiResponse.message || 'Error al obtener empresas')
		}

		return apiResponse.data
	}

	/**
	 * Crea una nueva empresa
	 */
	static async createCompany(data: Pick<Company, 'code' | 'name' | 'active'>): Promise<Company> {
		const response = await api.post<APIResponse<Company>>('/companies', data)
		const apiResponse = response.data

		// Verificar si la respuesta es exitosa
		if (!apiResponse.success || !apiResponse.data) {
			throw new Error(apiResponse.message || 'Error al crear empresa')
		}

		return apiResponse.data
	}

	/**
	 * Actualiza una empresa
	 */
	static async updateCompany(data: Partial<Company>): Promise<Company> {
		const response = await api.put<APIResponse<Company>>(`/companies`, data).catch((error) => {
			console.log(error)
			throw new Error(error.response.data.message || 'Error al actualizar empresa')
		})
		const apiResponse = response.data

		// Verificar si la respuesta es exitosa
		if (!apiResponse.success || !apiResponse.data) {
			throw new Error(apiResponse.message || 'Error al actualizar empresa')
		}

		return apiResponse.data
	}

	/**
	 * Obtiene las feature flags de una empresa
	 */
	static async getCompanyFeatures(companyID: string): Promise<Record<string, string>> {
		const response = await api.get<APIResponse<Record<string, string>>>('/companies/features', {
			params: { companyID },
		})
		const apiResponse = response.data

		if (!apiResponse.success || !apiResponse.data) {
			throw new Error(apiResponse.message || 'Error al obtener features de la empresa')
		}

		return apiResponse.data
	}

	/**
	 * Establece una feature flag de una empresa
	 */
	static async setCompanyFeature(companyID: string, feature: string, value: string): Promise<void> {
		const response = await api.post<APIResponse<void>>('/companies/features', {
			companyID,
			feature,
			value,
		})
		const apiResponse = response.data

		if (!apiResponse.success) {
			throw new Error(apiResponse.message || 'Error al establecer feature de la empresa')
		}
	}
}
